import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import { ExternalLink, FileText, Search } from "lucide-react";

import { apiGet } from "../../Api/Client.js";
import { prefetchPages } from "./docsCorpus.js";
import { decodeSlugPath } from "./docsSlug.js";
import "../../Styles/DocsPortalScreen.css";

// A slug may point into a subfolder ("Company/PayrollProcess.md"). Encoding
// the whole string would turn "/" into "%2F", which the backend's <path:...>
// route does not treat as a real path separator — so each segment is encoded
// on its own and rejoined with a literal "/".
function encodeSlugPath(slug) {
  return (slug || "").split("/").map(encodeURIComponent).join("/");
}

// No project segment in the URL falls back to this one — the only way that
// happens now is a link saved before project became a path segment
// (2026-10-07): a bare old "/docs/<slug>" or an old "?project=" query link.
// Every current link, from the sidebar or from "Suggest an edit", carries
// its project as the segment right after "/docs/".
const DEFAULT_PROJECT = "intranet";

// Kept in sync with the keys in Backend/EnterpriseCore/docs_portal_views.py's
// PROJECTS by hand, same as App.jsx's sidebar paths already are — there's no
// API call this screen could make before it knows which project is even in
// the URL, so there's no way to fetch this list instead of naming it here.
const KNOWN_PROJECTS = ["company", "intranet", "interviewgod", "banao", "vikaas", "vidya"];

const MIN_SIDEBAR = 180;
const MAX_SIDEBAR = 480;
const DEFAULT_SIDEBAR = 240;
const SIDEBAR_WIDTH_KEY = "docs_portal_sidebar_width";

// A short, readable excerpt around the match — markdown syntax stripped so it
// reads as prose, not source.
function snippetFor(raw, q) {
  const clean = (raw || "").replace(/[#*_`>]/g, " ").replace(/\s+/g, " ").trim();
  const lower = clean.toLowerCase();
  const idx = lower.indexOf(q);
  if (idx === -1) return clean.slice(0, 140) + (clean.length > 140 ? "…" : "");
  const start = Math.max(0, idx - 50);
  const end = Math.min(clean.length, idx + q.length + 90);
  let snippet = clean.slice(start, end);
  if (start > 0) snippet = "…" + snippet;
  if (end < clean.length) snippet = snippet + "…";
  return snippet;
}

// Pulled live from the repo's Docs folder and rendered here, in the app's own
// look — see Backend/EnterpriseCore/docs_portal_views.py for the grouping and
// the GitHub fetch. Read-only by design: change the source file and open a
// PR (the pencil icon on every page does exactly that), don't edit here.
export function DocsPortalScreen({ route, navigate }) {
  // The project is the path segment right after "/docs/" — deterministic:
  // the same "/docs/<project>/<slug>" shape for every project, including
  // Intranet, rather than Intranet being the unmarked default and everyone
  // else carrying a query param (the old scheme, which made the URL format
  // depend on which project you were looking at). A slug may itself contain
  // "/" for a subfolder, which is exactly why project has to be its own
  // segment rather than inferred from the path shape — segments[0] is always
  // the project, full stop, no parsing ambiguity with the slug after it.
  //
  // The fallback branch exists only for a link saved before this scheme
  // shipped (2026-10-07): a bare old "/docs/<slug>" with no project segment
  // at all, or an old "?project=<x>" query link. Both still resolve exactly
  // as they always did, so nothing already shared or bookmarked breaks.
  const { project, slug } = useMemo(() => {
    const [pathPart, queryPart] = (route || "").split("?");
    const segments = pathPart.split("/").filter(Boolean).slice(1); // drop leading "docs"
    if (segments.length && KNOWN_PROJECTS.includes(segments[0])) {
      return { project: segments[0], slug: decodeSlugPath(segments.slice(1).join("/")) };
    }
    const legacyProject = new URLSearchParams(queryPart || "").get("project") || DEFAULT_PROJECT;
    return { project: legacyProject, slug: decodeSlugPath(segments.join("/")) };
  }, [route]);

  const [nav, setNav] = useState(null);
  const [navError, setNavError] = useState("");
  const [projects, setProjects] = useState([]);

  const [page, setPage] = useState(null);
  const [pageError, setPageError] = useState("");
  const [pageLoading, setPageLoading] = useState(false);

  // Full-text search over a ~20-file corpus — small enough to just fetch once
  // in the background and search in the browser, rather than standing up a
  // separate search index for it. Keeps both cases: lower for matching,
  // original for readable snippets. Keyed by slug alone, not project — it's
  // reset below on every project switch, so no cross-project collision risk.
  const [query, setQuery] = useState("");
  const corpus = useRef({}); // slug -> { raw, lower }
  const [corpusVersion, setCorpusVersion] = useState(0);

  // Resizable sidebar — width remembered across visits, same pattern the main
  // nav's own collapse state already uses (atg.nav.collapsed).
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = Number(localStorage.getItem(SIDEBAR_WIDTH_KEY));
    return saved >= MIN_SIDEBAR && saved <= MAX_SIDEBAR ? saved : DEFAULT_SIDEBAR;
  });
  const [resizing, setResizing] = useState(false);
  const dragState = useRef(null);

  const onResizeStart = (e) => {
    e.preventDefault();
    dragState.current = { startX: e.clientX, startWidth: sidebarWidth };
    setResizing(true);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", onResizeMove);
    window.addEventListener("mouseup", onResizeEnd);
  };
  const onResizeMove = (e) => {
    if (!dragState.current) return;
    const next = Math.min(MAX_SIDEBAR, Math.max(MIN_SIDEBAR, dragState.current.startWidth + (e.clientX - dragState.current.startX)));
    setSidebarWidth(next);
  };
  const onResizeEnd = () => {
    dragState.current = null;
    setResizing(false);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
    window.removeEventListener("mousemove", onResizeMove);
    window.removeEventListener("mouseup", onResizeEnd);
    setSidebarWidth((w) => { localStorage.setItem(SIDEBAR_WIDTH_KEY, String(w)); return w; });
  };

  useEffect(() => {
    // Set when the person leaves this project: late answers are then ignored, so one project's
    // page text never lands in another project's search index.
    let stale = false;
    const isCurrent = () => !stale;
    setNav(null);
    setNavError("");
    corpus.current = {};
    setCorpusVersion((v) => v + 1);
    apiGet(`/EnterpriseCore/docs-portal/${encodeURIComponent(project)}/nav/`)
      .then((res) => {
        if (stale) return;
        const groups = res.groups || [];
        setNav(groups);
        if (res.projects) setProjects(res.projects);
        const allSlugs = groups.flatMap((g) => g.pages.map((p) => p.slug));
        prefetchPages(
          allSlugs,
          (s) => apiGet(`/EnterpriseCore/docs-portal/${encodeURIComponent(project)}/page/${encodeSlugPath(s)}/`),
          isCurrent,
          (s, page) => {
            corpus.current[s] = { raw: page.body || "", lower: (page.body || "").toLowerCase() };
            setCorpusVersion((v) => v + 1);
          },
        );
      })
      .catch(() => { if (!stale) setNavError("Could not load the documentation index."); });
    return () => { stale = true; };
  }, [project]);

  useEffect(() => {
    if (!slug) { setPage(null); return undefined; }
    let stale = false;
    setPageLoading(true);
    setPageError("");
    apiGet(`/EnterpriseCore/docs-portal/${encodeURIComponent(project)}/page/${encodeSlugPath(slug)}/`)
      .then((res) => {
        if (stale) return;
        setPage(res);
        corpus.current[slug] = { raw: res.body || "", lower: (res.body || "").toLowerCase() };
      })
      .catch((err) => { if (!stale) setPageError(err?.payload?.detail || err?.message || "Could not load this page."); })
      .finally(() => { if (!stale) setPageLoading(false); });
    return () => { stale = true; };
  }, [project, slug]);

  // Every project, including Intranet, gets the same "/docs/<project>/<slug>"
  // shape — no more special-casing the default project out of the URL.
  const openPage = (pageSlug) => { setQuery(""); navigate(`/docs/${project}/${pageSlug}`); };

  const q = query.trim().toLowerCase();
  const searching = q.length > 0;

  const visibleGroups = useMemo(() => {
    if (!nav) return null;
    if (!q) return nav;
    return nav
      .map((group) => ({
        ...group,
        pages: group.pages.filter((p) =>
          p.title.toLowerCase().includes(q) || (corpus.current[p.slug]?.lower || "").includes(q)
        ),
      }))
      .filter((group) => group.pages.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nav, q, corpusVersion]);

  const searchResults = useMemo(() => {
    if (!searching || !visibleGroups) return [];
    return visibleGroups.flatMap((group) =>
      group.pages.map((p) => ({
        ...p,
        group: group.label,
        snippet: snippetFor(corpus.current[p.slug]?.raw, q),
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searching, visibleGroups, q, corpusVersion]);

  return (
    <section className="Docs-Portal Screen-Stack">
      {/* No page-level "Documents" heading here — the sidebar link the
          person just clicked already says that; repeating it read as a
          redundant second label rather than useful context. */}
      <div className={"Docs-Portal-Body" + (resizing ? " resizing" : "")}>
        <div className="Docs-Portal-Sidebar-Wrap" style={{ width: sidebarWidth, flexBasis: sidebarWidth }}>
        <nav className="Docs-Portal-Sidebar">
          {/* No in-page project switcher — each project is now its own
              sidebar link AND its own access-controlled page (2026-10-07).
              A dropdown here would let anyone who can open this screen at
              all navigate straight to a project they don't actually have a
              sidebar link for, which defeats the point of gating them
              separately even though the content fetch would still 403. */}
          <div className="Docs-Portal-Search">
            <Search size={14} />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search documentation…" />
          </div>

          {navError && <p className="Docs-Portal-Error">{navError}</p>}
          {!navError && !nav && <p className="Docs-Portal-Muted">Loading index…</p>}
          {visibleGroups && visibleGroups.length === 0 && (
            <p className="Docs-Portal-Muted">No pages match "{query.trim()}".</p>
          )}
          {visibleGroups && visibleGroups.map((group) => (
            <div key={group.label} className="Docs-Portal-Group">
              <p className="Docs-Portal-Group-Label">{group.label}</p>
              {group.pages.map((p) => (
                <button
                  key={p.slug}
                  type="button"
                  className={"Docs-Portal-NavItem" + (p.slug === slug ? " active" : "")}
                  onClick={() => openPage(p.slug)}
                >
                  <FileText size={13} />
                  <span>{p.title}</span>
                </button>
              ))}
            </div>
          ))}

        </nav>

        {/* Sibling to the sidebar, not nested inside it — the sidebar clips
            overflow-x to keep long titles from forcing a horizontal
            scrollbar, which would also clip this handle if it lived inside
            that box. Invisible at rest; a thin line appears on hover/drag so
            the resize affordance is discoverable without adding permanent
            visual clutter to every page load. */}
        <div
          className={"Docs-Portal-Resize-Handle" + (resizing ? " active" : "")}
          onMouseDown={onResizeStart}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize the documentation sidebar"
        />
        </div>

        <div className="Docs-Portal-Content">
          {searching && (
            <div className="Docs-Portal-Results">
              <p className="Docs-Portal-Results-Count">
                {searchResults.length} {searchResults.length === 1 ? "result" : "results"} for "{query.trim()}"
              </p>
              {searchResults.map((r) => (
                <button key={r.slug} type="button" className="Docs-Portal-Result" onClick={() => openPage(r.slug)}>
                  <div className="Docs-Portal-Result-Title">
                    <FileText size={13} />
                    <span>{r.title}</span>
                    <span className="Docs-Portal-Result-Group">{r.group}</span>
                  </div>
                  <p className="Docs-Portal-Result-Snippet">{r.snippet}</p>
                </button>
              ))}
            </div>
          )}

          {!searching && !slug && (
            <div className="Docs-Portal-Welcome">
              <h2>{projects.find((p) => p.key === project)?.label || "Documentation"}</h2>
              <p>Pulled live from the repo — pick a page from the left to start reading.</p>
            </div>
          )}

          {!searching && slug && pageLoading && <p className="Docs-Portal-Muted">Loading…</p>}
          {!searching && slug && pageError && <p className="Docs-Portal-Error">{pageError}</p>}

          {!searching && slug && page && !pageLoading && (
            <article className="Docs-Portal-Article">
              <div className="Docs-Portal-Article-Head">
                <h2>{page.title}</h2>
                {page.edit_url && (
                  <a href={page.edit_url} target="_blank" rel="noopener noreferrer" className="Docs-Portal-Edit-Link">
                    Suggest an edit <ExternalLink size={12} />
                  </a>
                )}
              </div>
              <div className="Docs-Portal-Markdown">
                {/* Raw HTML in a page is allowed only through the sanitizer (GitHub's own allow-list), so
                    collapsible <details> sections work and nothing a page contains can run a script. */}
                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw, rehypeSanitize]}>{page.body}</ReactMarkdown>
              </div>
            </article>
          )}
        </div>
      </div>
    </section>
  );
}
